import { EntityNode } from '@common/ProjectBrowser/Nodes';
import { Entity } from '@common/types/Entity';
import { useGenerateJobMutation } from '@pages/Transpiler/useGenerateJobMutation';
import { BackEndCodeLanguage } from '@prophecy/graphql-types/enums';
import { CheckIcon, DotsVerticalIcon, LoadingDIcon, PlusIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Button, Dialog, OverlaySpinner, Stack, Tooltip } from '@prophecy/ui-v3';
import { message } from '@prophecy/ui-v3/ui-compat';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

import { isUniqueID, uniqueIDWithDelimiter } from '../../../../../utils';
import { FileFormats } from '../../../config';
import { useImportState } from '../../useImportState';
import { ActionTypes, FileTypes, TreeItem } from '../types';
import { DeleteEntityDropdown, formatPipelineName, getTotalFolderCounts, PipelineExtensions } from '../utils';
import { ChoosePsetConfig } from './ChoosePsetConfig';
import { MoveToFolder } from './MoveToFolder';

const StyledStack = styled(Stack)`
  visibility: hidden;
`;

const StyledStackContainer = styled(Stack)`
  &:hover ${StyledStack} {
    visibility: visible;
  }
`;

const StyledNode = styled(EntityNode)<{ readonly selectable?: boolean }>`
  ${({ selectable }) =>
    selectable
      ? ''
      : `
    &&& div{
      color: ${theme.colors.gray500};  
    }
    
  `}
  &:hover ${StyledStack} {
    visibility: visible;
  }
`;
const MoreActions = ({
  isNodeAdded,
  onClick,
  loading,
  toggleMovePanel,
  selectable,
  name
}: {
  isNodeAdded: boolean;
  onClick?: () => void;
  loading?: boolean;
  toggleMovePanel: () => void;
  selectable?: boolean;
  name: string;
}) => {
  const {
    transpilerState: { uploadedFilePaths }
  } = useImportState();
  const totalFolders = useMemo(() => getTotalFolderCounts(uploadedFilePaths), [uploadedFilePaths]);
  return (
    <StyledStack gap={theme.spaces.x4} direction='horizontal' alignY='center'>
      {selectable && (
        <>
          {isNodeAdded ? (
            <CheckIcon type='default' size='xs' color={theme.colors.gray400} />
          ) : (
            <Button
              disabled={loading}
              variant='plain'
              icon={
                loading ? (
                  <LoadingDIcon type='extended' size='xs' color={theme.colors.brand500} />
                ) : (
                  <PlusIcon type='default' size='xs' color={theme.colors.gray500} />
                )
              }
              aria-label={`Add ${name}`}
              data-test-id='folder-entity-node-add-entity-button'
            />
          )}
        </>
      )}
      <DeleteEntityDropdown
        title='Delete File'
        name={name}
        moveDisabled={!totalFolders}
        hasDeleteDialog={false}
        hasMove={true}
        onMove={() => toggleMovePanel()}
        icon={<DotsVerticalIcon type='default' />}
        onClick={onClick}
        loading={loading}
      />
    </StyledStack>
  );
};

export const FolderEntityNode = ({ node }: { node: TreeItem }) => {
  const isLeafNode = node.isLeaf;
  const [hasToggle, toggle] = useState(false);
  const [hasMovePanel, toggleMovePanel] = useState(false);
  const {
    loading,
    isDeleting,
    transpilerState: {
      transpilerId,
      transpilerEntityTypes: { jobs, pipelines }
    },
    formDataWithActions: {
      formContext: { getValues },
      setRecommendedPath
    },
    actions: { updateTranspiledPipeline, removeFile }
  } = useImportState();
  const isAbinitio = getValues('source') === FileFormats.abinitio;
  const { isLoading: addingJob, mutateAsync: createJob } = useGenerateJobMutation(transpilerId);

  const nodeName = node.key as string;
  const isSql = getValues('language') === BackEndCodeLanguage.sql;
  const displayEntity = isSql ? Entity.Model : Entity.Pipeline;
  const title = node.title as string;
  const fileType = node.fileType as FileTypes;
  const selectable = node.selectable;
  const isPipelineNode = PipelineExtensions.includes(fileType);
  const isJobNode = fileType === FileTypes.plan;
  const currentEntity = isPipelineNode || !isLeafNode ? Entity.Pipeline : isJobNode ? Entity.Job : undefined;

  if (isLeafNode) {
    const isPsetNode = fileType === FileTypes.pset;
    let isNodeAdded = false;
    if (selectable) {
      if (isJobNode) {
        isNodeAdded = jobs?.some((job) => job.filePath === nodeName);
      } else if (isPipelineNode) {
        isNodeAdded = pipelines?.some((pipeline) => pipeline.filePath === nodeName);
      }
    }

    const addEntityNode = async () => {
      if (loading || addingJob) {
        return;
      }

      if (isPipelineNode) {
        const name = formatPipelineName(title, fileType);
        const pipelineNames = pipelines.map((pipeline) => pipeline.name);
        if (isNodeAdded) {
          const allowedDialogResult = await Dialog.warning({
            title: `Duplicate ${displayEntity}`,
            subTitle: `This ${displayEntity} is already added, do you want to add it again?`,
            'data-test-id': 'transpiler-duplicate-entity-dialog'
          });
          if (allowedDialogResult !== Dialog.ResponseAction.okButton) {
            return;
          }
        }
        const obj = {
          name: isUniqueID(name, pipelines as unknown as { [key: string]: string }[], 'name')
            ? name
            : uniqueIDWithDelimiter(name, pipelineNames),
          filePath: nodeName,
          __pathIndex: pipelines.length,
          enabled: true
        };
        updateTranspiledPipeline(0, obj, ActionTypes.add);
        if (isAbinitio) {
          setRecommendedPath(nodeName);
        }
      } else if (isJobNode) {
        await createJob(nodeName);
        if (isAbinitio) {
          setRecommendedPath(nodeName);
        }
      } else if (isPsetNode) {
        if (pipelines.length === 0) {
          message.error({ content: 'Parameter Set can be added inside a pipeline. Please add the pipeline first.' });
        } else {
          toggle(true);
        }
      }
    };

    const isLoading = addingJob || isDeleting;

    return (
      <>
        {hasMovePanel && <MoveToFolder pathToMove={nodeName} onClose={() => toggleMovePanel(false)} />}
        {hasToggle && <ChoosePsetConfig toggle={() => toggle(!hasToggle)} name={nodeName} />}
        {isLoading && <OverlaySpinner spinning={true} />}
        <Tooltip title={node.selectable && !node.isLeaf ? '' : 'File not supported'}>
          <StyledNode
            dataKey={node.key}
            title={node.title}
            entityId={title}
            disabled={isLoading}
            actions={
              <MoreActions
                selectable={selectable}
                isNodeAdded={isNodeAdded}
                name={title}
                toggleMovePanel={() => toggleMovePanel(true)}
                onClick={() => removeFile(node.title as string, node.key as string, currentEntity)}
                loading={isLoading}
              />
            }
            {...node}
            onClick={selectable ? addEntityNode : void 0}
            entityName={fileType}
            nodeIcon={null}
          />
        </Tooltip>
      </>
    );
  }
  return (
    <StyledStackContainer direction='horizontal' width='100%' gap={theme.spaces.x4} align='space-between'>
      {node.title}
      {hasMovePanel && <MoveToFolder pathToMove={nodeName} onClose={() => toggleMovePanel(false)} />}
      <MoreActions
        selectable={false}
        isNodeAdded={false}
        toggleMovePanel={() => toggleMovePanel(true)}
        name={title}
        onClick={() => removeFile(node.title as string, node.key as string, currentEntity)}
      />
    </StyledStackContainer>
  );
};
